import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useDebounce } from "use-debounce";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  GradebookActivityType,
  GradebookTotalMode,
  StudentActivityStatus,
  UnifiedSubmission,
  useAssignActivityToGradeWeightingCategoryMutation,
  useAllowLateAssignmentSubmissionMutation,
  useAllowLateProjectSubmissionMutation,
  useClearAssignmentSubmissionStatusMutation,
  useClearProjectSubmissionStatusMutation,
  useCreateGradeWeightingCategoryMutation,
  useDeleteGradeWeightingCategoryMutation,
  useExcuseAssignmentSubmissionMutation,
  useExcuseProjectSubmissionMutation,
  useGetCoursesByFacultyQuery,
  useGetCoursesBySchoolByQueryQuery,
  useGetGradebookExportFileQuery,
  useGetGradebookQuery,
  useGetGradeWeightingConfigQuery,
  useMarkAssignmentSubmissionMissingMutation,
  useMarkProjectSubmissionMissingMutation,
  useRequestGradebookExportMutation,
  useSetGradebookActivityMaxPointsMutation,
  useSetGradeWeightingEnabledMutation,
  useSetAssignmentStatusForStudentMutation,
  useSetProjectStatusForStudentMutation,
  useUnassignActivityFromGradeWeightingCategoryMutation,
  useUpdateGradeWeightingCategoryMutation,
  useExcuseAssessmentSubmissionMutation,
  useAllowLateAssessmentSubmissionMutation,
  useMarkAssessmentSubmissionMissingMutation,
  useClearAssessmentSubmissionStatusMutation,
  useSetAssessmentStatusForStudentMutation,
} from "@axio/graphql/generated/graphql";

import { useAppConfig } from "../../AppConfigProvider";
import { useAuth } from "../../hooks/useAuth";
import { GradingCenterModalState } from "../../components/adminDashboard/GradingCenter/types";
import { useGradingCenterSubmissions } from "../../contexts/GradingCenterContext";
import FacultyGradebook from "../../components/adminDashboard/Gradebook/FacultyGradebook";
import type { GradebookStudent } from "../../components/adminDashboard/Gradebook/GradebookCell";
import type { RootStackParamList } from "../../navigation/navigation.types";
import WeightingDialog from "../../components/adminDashboard/Gradebook/WeightingDialog";
import type {
  DraftWeightingCategory,
  DraftWeightingConfig,
  GradeWeightingConfigData,
  WeightingActivity,
} from "../../components/adminDashboard/Gradebook/gradebookWeighting.types";
import { toDraftWeightingConfig } from "../../components/adminDashboard/Gradebook/gradebookWeighting.utils";
import Sentry from "../../utils/sentry";
import useFileFetch from "../../hooks/useFileFetch";
import { hasMinRole, UserRoleUI } from "../../constants/rolesAndPermissions";

const EXPORT_POLLING_INTERVAL_MS = 3000;
const PENDING_EXPORT_STORAGE_KEY = "pendingGradebookExport";
const safeSessionStorage = (typeof window !== "undefined" && window.sessionStorage) || null;

type CategoryActivityEntry = {
  activityId: string;
  activityType: WeightingActivity["activityType"];
};

interface FacultyGradebookContainerProps {
  modalState: GradingCenterModalState;
  setModalState: Dispatch<SetStateAction<GradingCenterModalState>>;
}

function FacultyGradebookContainer({ modalState, setModalState }: FacultyGradebookContainerProps) {
  const { setSelectedSubmissionData, setFilteredSubmissions } = useGradingCenterSubmissions();
  const appConfig = useAppConfig();
  const { userInfo } = useAuth();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const schoolId = appConfig?.axioSchools?.[0] ?? "";
  const facultyId = userInfo.userId;

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 350);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [moduleDropdownOpen, setModuleDropdownOpen] = useState(false);
  const [weightingOpen, setWeightingOpen] = useState(false);
  const [weightingSaveError, setWeightingSaveError] = useState<string | null>(null);
  const [weightingSaving, setWeightingSaving] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportState, setExportState] = useState<{
    isGenerating: boolean;
    fileURL: string;
    fileId: string | null;
  }>({ isGenerating: false, fileURL: "", fileId: null });
  const [pendingExportMetadata, setPendingExportMetadata] = useState<{ previousFileId: string | null } | null>(null);
  const restoredExportRef = useRef(false);

  const isAdmin = hasMinRole(userInfo.userRole, UserRoleUI.SCHOOL_ADMIN);

  const { data: facultyCourseData } = useGetCoursesByFacultyQuery({
    variables: { schoolId, userId: facultyId },
    skip: isAdmin || !facultyId,
  });

  const { data: adminCourseData } = useGetCoursesBySchoolByQueryQuery({
    variables: { schoolId, searchQuery: "", page: 1, pageSize: 200 },
    skip: !isAdmin || !schoolId,
  });

  const courseList = useMemo(() => {
    const raw = isAdmin
      ? (adminCourseData?.getCoursesBySchoolByQuery?.data?.courses ?? [])
      : (facultyCourseData?.getCoursesByFaculty?.data ?? []);
    return raw.filter((course): course is NonNullable<typeof course> => course != null);
  }, [isAdmin, facultyCourseData, adminCourseData]);

  const firstCourseId = courseList[0]?.id ?? "";

  useEffect(() => {
    if (!selectedCourse && firstCourseId) {
      setSelectedCourse(firstCourseId);
    }
  }, [selectedCourse, firstCourseId]);

  // Reset module filter whenever the selected course changes.
  useEffect(() => {
    setSelectedModule("");
  }, [selectedCourse]);

  const courseId = selectedCourse || firstCourseId;

  useEffect(() => {
    if (restoredExportRef.current || !courseId) return;
    restoredExportRef.current = true;
    try {
      const raw = safeSessionStorage?.getItem(PENDING_EXPORT_STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as { courseId: string; previousFileId: string | null };
      if (stored.courseId === courseId) {
        setPendingExportMetadata({ previousFileId: stored.previousFileId });
      } else {
        safeSessionStorage?.removeItem(PENDING_EXPORT_STORAGE_KEY);
      }
    } catch {
      safeSessionStorage?.removeItem(PENDING_EXPORT_STORAGE_KEY);
    }
  }, [courseId]);

  const courseItems = useMemo(
    () => courseList.map((course) => ({ label: course.name ?? "", value: course.id })),
    [courseList]
  );

  const moduleItems = useMemo(() => {
    const items: { label: string; value: string }[] = [{ label: "All Modules", value: "" }];
    const course = courseList.find((c) => c.id === courseId);
    course?.modules?.forEach((module) => {
      if (module?.id && module.title) {
        items.push({ label: module.title, value: module.id });
      }
    });
    return items;
  }, [courseList, courseId]);

  const {
    data: gradebookData,
    loading,
    error,
    refetch: refetchGradebook,
  } = useGetGradebookQuery({
    variables: {
      input: {
        courseId,
        moduleId: selectedModule || undefined,
        searchQuery: debouncedSearchQuery.trim() || undefined,
      },
    },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
  });

  const onStatusMutationComplete = () => refetchGradebook();

  const [excuseAssignment] = useExcuseAssignmentSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [markAssignmentMissing] = useMarkAssignmentSubmissionMissingMutation({ onCompleted: onStatusMutationComplete });
  const [allowLateAssignment] = useAllowLateAssignmentSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [clearAssignmentStatus] = useClearAssignmentSubmissionStatusMutation({ onCompleted: onStatusMutationComplete });
  const [excuseAssessment] = useExcuseAssessmentSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [markAssessmentMissing] = useMarkAssessmentSubmissionMissingMutation({ onCompleted: onStatusMutationComplete });
  const [allowLateAssessment] = useAllowLateAssessmentSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [clearAssessmentStatus] = useClearAssessmentSubmissionStatusMutation({ onCompleted: onStatusMutationComplete });
  const [excuseProject] = useExcuseProjectSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [markProjectMissing] = useMarkProjectSubmissionMissingMutation({ onCompleted: onStatusMutationComplete });
  const [allowLateProject] = useAllowLateProjectSubmissionMutation({ onCompleted: onStatusMutationComplete });
  const [clearProjectStatus] = useClearProjectSubmissionStatusMutation({ onCompleted: onStatusMutationComplete });
  const [setAssignmentStatus] = useSetAssignmentStatusForStudentMutation({ onCompleted: onStatusMutationComplete });
  const [setAssessmentStatus] = useSetAssessmentStatusForStudentMutation({ onCompleted: onStatusMutationComplete });
  const [setProjectStatus] = useSetProjectStatusForStudentMutation({ onCompleted: onStatusMutationComplete });

  useEffect(() => {
    if (modalState === GradingCenterModalState.CLOSED) {
      refetchGradebook();
    }
  }, [modalState]);

  const gradebook = gradebookData?.getGradebook?.data ?? null;
  const hasRows = !!gradebook && gradebook.rows.length > 0;

  const { data: fullCourseGradebookData } = useGetGradebookQuery({
    variables: {
      input: {
        courseId,
      },
    },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: weightingData,
    refetch: refetchWeightingConfig,
    loading: weightingLoading,
  } = useGetGradeWeightingConfigQuery({
    variables: { courseId },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: gradebookExportFileData,
    refetch: fetchLatestGradebookExport,
    startPolling,
    stopPolling,
  } = useGetGradebookExportFileQuery({
    variables: { courseId },
    skip: !courseId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [setGradeWeightingEnabled] = useSetGradeWeightingEnabledMutation();
  const [createGradeWeightingCategory] = useCreateGradeWeightingCategoryMutation();
  const [updateGradeWeightingCategory] = useUpdateGradeWeightingCategoryMutation();
  const [deleteGradeWeightingCategory] = useDeleteGradeWeightingCategoryMutation();
  const [assignActivityToGradeWeightingCategory] = useAssignActivityToGradeWeightingCategoryMutation();
  const [unassignActivityFromGradeWeightingCategory] = useUnassignActivityFromGradeWeightingCategoryMutation();
  const [requestGradebookExport] = useRequestGradebookExportMutation();
  const [setGradebookActivityMaxPoints] = useSetGradebookActivityMaxPointsMutation();
  const { fetchAndOpenFile } = useFileFetch();

  const weightingConfig = useMemo(
    () => toDraftWeightingConfig(weightingData?.getGradeWeightingConfig.data, courseId, facultyId),
    [courseId, facultyId, weightingData]
  );

  const weightingActivities = useMemo<WeightingActivity[]>(
    () =>
      (fullCourseGradebookData?.getGradebook?.data?.columns ?? gradebook?.columns ?? []).map((column) => ({
        activityId: column.activityId,
        activityName: column.activityName,
        activityType: column.activityType,
        moduleName: column.moduleName,
        maxPoints: column.maxPoints,
      })),
    [fullCourseGradebookData, gradebook]
  );

  const selectedCourseName = useMemo(
    () => courseList.find((course) => course.id === courseId)?.name ?? "",
    [courseList, courseId]
  );

  const handleOpenPanel = (studentId: string, activityId: string, activityType: GradebookActivityType) => {
    const submissionType =
      activityType === GradebookActivityType.Assignment
        ? "Assignment"
        : activityType === GradebookActivityType.Assessment
          ? "Assessment"
          : "Project";
    const activityColumn = gradebook?.columns.find((c) => c.activityId === activityId);
    const activityName = activityColumn?.activityName ?? "";

    // Build the navigation list: all students who have a submission for this activity.
    const activitySubmissions = (gradebook?.rows ?? [])
      .filter((r) => !!r.cells.find((c) => c.activityId === activityId)?.submissionId)
      .map((r) => {
        const c = r.cells.find((cell) => cell.activityId === activityId)!;
        return {
          __typename: "UnifiedSubmission" as const,
          id: c.submissionId!,
          submissionType,
          created: "",
          moduleName: activityColumn?.moduleName ?? "",
          submissionTitle: activityName,
          courseName: "",
          courseId,
          student: {
            __typename: "StudentProperties" as const,
            id: r.student.id,
            name: r.student.name,
            email: r.student.email ?? "",
            initials: r.student.initials ?? "",
          },
          grade: null,
          status: c.status ?? null,
          dueDate: c.dueDate ?? null,
          submittedOn: c.submittedOn ?? null,
          isRetake: c.isRetake ?? null,
        };
      });

    const selectedRow = gradebook?.rows.find((r) => r.student.id === studentId);
    if (!selectedRow) return;

    const selectedCell = selectedRow.cells.find((c) => c.activityId === activityId);

    setFilteredSubmissions(activitySubmissions as unknown as UnifiedSubmission[]);
    setSelectedSubmissionData({
      __typename: "UnifiedSubmission",
      id: selectedCell?.submissionId ?? "",
      submissionType,
      created: "",
      moduleName: activityColumn?.moduleName ?? "",
      submissionTitle: activityName,
      courseName: selectedCourseName,
      courseId,
      student: {
        __typename: "StudentProperties",
        id: selectedRow.student.id,
        name: selectedRow.student.name,
        email: selectedRow.student.email ?? "",
        initials: selectedRow.student.initials ?? "",
      },
      grade: selectedCell?.percentageScore ?? null,
      status: selectedCell?.status ?? null,
      dueDate: selectedCell?.dueDate ?? null,
      submittedOn: selectedCell?.submittedOn ?? null,
      isRetake: selectedCell?.isRetake ?? null,
    } as unknown as UnifiedSubmission);

    if (activityType === GradebookActivityType.Assignment) {
      setModalState(GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE);
    } else if (activityType === GradebookActivityType.Assessment) {
      setModalState(GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE);
    } else {
      setModalState(GradingCenterModalState.VIEW_PROJECT_RESPONSE);
    }
  };

  const handleStudentPress = useCallback(
    (student: GradebookStudent) => {
      if (!courseId) return;
      navigation.navigate("Course Information For Particular Student", {
        courseId,
        courseName: selectedCourseName,
        studentId: student.student.id,
        studentName: student.student.name,
        returnTo: "Admin Grading Center",
      });
    },
    [navigation, courseId, selectedCourseName]
  );

  useEffect(() => {
    const exportResponse = gradebookExportFileData?.getGradebookExportFile;

    if (exportResponse?.success && exportResponse.data) {
      const file = exportResponse.data;

      if (pendingExportMetadata && file.id !== pendingExportMetadata.previousFileId) {
        setExportState({
          isGenerating: false,
          fileURL: file.fileURL ?? "",
          fileId: file.id,
        });
        safeSessionStorage?.removeItem(PENDING_EXPORT_STORAGE_KEY);
        setPendingExportMetadata(null);
        stopPolling?.();
        if (file.fileURL) {
          fetchAndOpenFile(file.fileURL, { disposition: "attachment" });
        }
      } else if (!exportState.isGenerating && !exportState.fileURL) {
        setExportState((prev) => ({
          ...prev,
          fileURL: file.fileURL ?? "",
          fileId: file.id,
        }));
        stopPolling?.();
      }
    }
  }, [
    gradebookExportFileData,
    pendingExportMetadata,
    stopPolling,
    exportState.isGenerating,
    exportState.fileURL,
    fetchAndOpenFile,
  ]);

  useEffect(() => () => stopPolling?.(), [stopPolling]);

  const handleExportGrades = useCallback(async () => {
    if (!courseId || exportState.isGenerating) return;

    try {
      setExportError(null);
      setExportState((prev) => ({ ...prev, isGenerating: true, fileURL: "", fileId: null }));

      const previousFileId = gradebookExportFileData?.getGradebookExportFile?.data?.id ?? null;
      safeSessionStorage?.setItem(PENDING_EXPORT_STORAGE_KEY, JSON.stringify({ courseId, previousFileId }));
      setPendingExportMetadata({ previousFileId });

      const res = await requestGradebookExport({
        variables: {
          input: {
            courseId,
            totalMode: GradebookTotalMode.Cumulative,
          },
        },
      });

      if (!res.data?.requestGradebookExport.success) {
        throw new Error(res.data?.requestGradebookExport.message || "Failed to start gradebook export");
      }

      await fetchLatestGradebookExport();
      stopPolling?.();
      startPolling?.(EXPORT_POLLING_INTERVAL_MS);
    } catch (err) {
      Sentry.captureException(err);
      setExportError(err instanceof Error ? err.message : "Failed to start gradebook export");
      setExportState((prev) => ({ ...prev, isGenerating: false }));
      safeSessionStorage?.removeItem(PENDING_EXPORT_STORAGE_KEY);
      setPendingExportMetadata(null);
      stopPolling?.();
    }
  }, [
    courseId,
    exportState.isGenerating,
    fetchLatestGradebookExport,
    gradebookExportFileData,
    requestGradebookExport,
    startPolling,
    stopPolling,
  ]);

  const assertConfigResponse = (config: GradeWeightingConfigData | null | undefined, fallbackMessage: string) => {
    if (!config) {
      throw new Error(fallbackMessage);
    }

    return toDraftWeightingConfig(config, courseId, facultyId);
  };

  const categoryActivityEntries = (category: DraftWeightingCategory): CategoryActivityEntry[] => [
    ...category.assignmentIds.map((activityId) => ({
      activityId,
      activityType: GradebookActivityType.Assignment,
    })),
    ...category.assessmentIds.map((activityId) => ({
      activityId,
      activityType: GradebookActivityType.Assessment,
    })),
    ...category.projectIds.map((activityId) => ({
      activityId,
      activityType: GradebookActivityType.Project,
    })),
  ];

  const handleSaveWeighting = useCallback(
    async (draft: DraftWeightingConfig) => {
      if (!courseId) return;

      try {
        setWeightingSaving(true);
        setWeightingSaveError(null);

        let latestConfig = weightingConfig;
        if (latestConfig.enabled) {
          const disabledResponse = await setGradeWeightingEnabled({ variables: { courseId, enabled: false } });
          latestConfig = assertConfigResponse(
            disabledResponse.data?.setGradeWeightingEnabled.data,
            disabledResponse.data?.setGradeWeightingEnabled.message || "Failed to disable weighting before saving"
          );
        }

        const latestCategoryIds = new Set(latestConfig.categories.map((category) => category.id));
        const draftExistingCategoryIds = new Set(
          draft.categories.filter((category) => latestCategoryIds.has(category.id)).map((category) => category.id)
        );
        const categoryIdMap = new Map<string, string>();

        for (const category of draft.categories) {
          if (latestCategoryIds.has(category.id)) {
            const response = await updateGradeWeightingCategory({
              variables: {
                input: {
                  categoryId: category.id,
                  name: category.name,
                  weight: category.weight,
                  sortOrder: category.sortOrder,
                },
              },
            });
            latestConfig = assertConfigResponse(
              response.data?.updateGradeWeightingCategory.data,
              response.data?.updateGradeWeightingCategory.message || "Failed to update weighting category"
            );
            categoryIdMap.set(category.id, category.id);
          } else {
            const previousCategoryIds = new Set(latestConfig.categories.map((candidate) => candidate.id));
            const response = await createGradeWeightingCategory({
              variables: {
                input: {
                  courseId,
                  name: category.name,
                  weight: category.weight,
                  sortOrder: category.sortOrder,
                },
              },
            });
            latestConfig = assertConfigResponse(
              response.data?.createGradeWeightingCategory.data,
              response.data?.createGradeWeightingCategory.message || "Failed to create weighting category"
            );
            const createdCategory = latestConfig.categories.find((candidate) => !previousCategoryIds.has(candidate.id));
            if (!createdCategory) {
              throw new Error(`Failed to locate created category ${category.name}`);
            }
            categoryIdMap.set(category.id, createdCategory.id);
          }
        }

        for (const category of latestConfig.categories) {
          if (!draftExistingCategoryIds.has(category.id) && ![...categoryIdMap.values()].includes(category.id)) {
            const response = await deleteGradeWeightingCategory({ variables: { categoryId: category.id } });
            latestConfig = assertConfigResponse(
              response.data?.deleteGradeWeightingCategory.data,
              response.data?.deleteGradeWeightingCategory.message || "Failed to delete weighting category"
            );
          }
        }

        for (const currentCategory of latestConfig.categories) {
          for (const entry of categoryActivityEntries(currentCategory)) {
            await unassignActivityFromGradeWeightingCategory({
              variables: {
                input: {
                  courseId,
                  activityId: entry.activityId,
                  activityType: entry.activityType,
                },
              },
            });
          }
        }

        for (const category of draft.categories) {
          const categoryId = categoryIdMap.get(category.id);
          if (!categoryId) continue;

          for (const entry of categoryActivityEntries(category)) {
            await assignActivityToGradeWeightingCategory({
              variables: {
                input: {
                  categoryId,
                  activityId: entry.activityId,
                  activityType: entry.activityType,
                },
              },
            });
          }
        }

        const enabledResponse = await setGradeWeightingEnabled({
          variables: { courseId, enabled: draft.enabled },
        });
        assertConfigResponse(
          enabledResponse.data?.setGradeWeightingEnabled.data,
          enabledResponse.data?.setGradeWeightingEnabled.message || "Failed to update weighting mode"
        );

        await refetchWeightingConfig();
        setWeightingOpen(false);
      } catch (err) {
        Sentry.captureException(err);
        setWeightingSaveError(err instanceof Error ? err.message : "Failed to save grade weighting");
      } finally {
        setWeightingSaving(false);
      }
    },
    [
      assignActivityToGradeWeightingCategory,
      courseId,
      createGradeWeightingCategory,
      deleteGradeWeightingCategory,
      facultyId,
      refetchWeightingConfig,
      setGradeWeightingEnabled,
      unassignActivityFromGradeWeightingCategory,
      updateGradeWeightingCategory,
      weightingActivities,
      weightingConfig,
    ]
  );

  const handleMaxPointsChange = useCallback(
    (activityId: string, activityType: GradebookActivityType, maxPoints: number) => {
      setGradebookActivityMaxPoints({
        variables: { activityId, activityType, maxPoints },
      });
    },
    [setGradebookActivityMaxPoints]
  );

  const handleCellStatusAction = useCallback(
    (
      studentId: string,
      activityId: string,
      submissionId: string | null | undefined,
      activityType: GradebookActivityType,
      status: StudentActivityStatus
    ) => {
      if (submissionId) {
        const vars = { variables: { submissionId } };
        if (activityType === GradebookActivityType.Assignment) {
          if (status === StudentActivityStatus.Excused) excuseAssignment(vars);
          else if (status === StudentActivityStatus.Missing) markAssignmentMissing(vars);
          else if (status === StudentActivityStatus.LateAllowed) allowLateAssignment(vars);
          else if (status === StudentActivityStatus.Cleared) clearAssignmentStatus(vars);
        } else if (activityType === GradebookActivityType.Assessment) {
          if (status === StudentActivityStatus.Excused) excuseAssessment(vars);
          else if (status === StudentActivityStatus.Missing) markAssessmentMissing(vars);
          else if (status === StudentActivityStatus.LateAllowed) allowLateAssessment(vars);
          else if (status === StudentActivityStatus.Cleared) clearAssessmentStatus(vars);
        } else if (activityType === GradebookActivityType.Project) {
          if (status === StudentActivityStatus.Excused) excuseProject(vars);
          else if (status === StudentActivityStatus.Missing) markProjectMissing(vars);
          else if (status === StudentActivityStatus.LateAllowed) allowLateProject(vars);
          else if (status === StudentActivityStatus.Cleared) clearProjectStatus(vars);
        }
      } else {
        if (activityType === GradebookActivityType.Assignment)
          setAssignmentStatus({ variables: { studentId, assignmentId: activityId, status } });
        else if (activityType === GradebookActivityType.Assessment)
          setAssessmentStatus({ variables: { studentId, assessmentId: activityId, status } });
        else if (activityType === GradebookActivityType.Project)
          setProjectStatus({ variables: { studentId, projectId: activityId, status } });
      }
    },
    [
      excuseAssignment,
      markAssignmentMissing,
      allowLateAssignment,
      clearAssignmentStatus,
      excuseAssessment,
      markAssessmentMissing,
      allowLateAssessment,
      clearAssessmentStatus,
      excuseProject,
      markProjectMissing,
      allowLateProject,
      clearProjectStatus,
      setAssignmentStatus,
      setAssessmentStatus,
      setProjectStatus,
    ]
  );

  return (
    <>
      <FacultyGradebook
        courseItems={courseItems}
        moduleItems={moduleItems}
        selectedCourse={selectedCourse}
        selectedModule={selectedModule}
        setSelectedCourse={setSelectedCourse}
        setSelectedModule={setSelectedModule}
        courseDropdownOpen={courseDropdownOpen}
        moduleDropdownOpen={moduleDropdownOpen}
        setCourseDropdownOpen={setCourseDropdownOpen}
        setModuleDropdownOpen={setModuleDropdownOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        gradebook={gradebook}
        loading={loading}
        errorMessage={error?.message}
        hasCourse={!!courseId}
        hasRows={hasRows}
        weightingConfig={weightingConfig}
        weightingDisabled={!courseId || weightingLoading}
        exportDisabled={!courseId}
        exportLoading={exportState.isGenerating}
        exportErrorMessage={exportError}
        onOpenWeighting={() => setWeightingOpen(true)}
        onExportGrades={handleExportGrades}
        onOpenPanel={handleOpenPanel}
        onStudentPress={handleStudentPress}
        onMaxPointsChange={handleMaxPointsChange}
        onCellStatusAction={handleCellStatusAction}
      />
      <WeightingDialog
        visible={weightingOpen}
        config={weightingConfig}
        activities={weightingActivities}
        saving={weightingSaving}
        errorMessage={weightingSaveError}
        onClose={() => setWeightingOpen(false)}
        onSave={handleSaveWeighting}
      />
    </>
  );
}

export default FacultyGradebookContainer;
